import { useState, useEffect } from 'react';
import { FiClock, FiCheck } from 'react-icons/fi';
import { timeEntriesAPI } from '../../services/api';
import { useApp } from '../../context/AppContext';
import ConfirmDialog from '../common/ConfirmDialog';
import TimeEntriesModal from './TimeEntriesModal';

const TimeClockButton = () => {
  const { addNotification } = useApp();
  const [activeEntry, setActiveEntry] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [elapsedTime, setElapsedTime] = useState('00:00');
  const [todayTotalMinutes, setTodayTotalMinutes] = useState(0);
  const [showClockOutConfirm, setShowClockOutConfirm] = useState(false);
  const [showEntriesModal, setShowEntriesModal] = useState(false);
  const [isGettingLocation, setIsGettingLocation] = useState(false);

  // Function to get current location (required for clock in/out)
  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Tu navegador no soporta geolocalización. No puedes fichar sin ubicación.'));
        return;
      }

      setIsGettingLocation(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setIsGettingLocation(false);
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
        },
        (error) => {
          setIsGettingLocation(false);

          let errorMsg = 'No se pudo obtener la ubicación';
          if (error.code === 1) {
            errorMsg = 'Debes permitir el acceso a tu ubicación para poder fichar. Activa los permisos de ubicación en tu navegador.';
          } else if (error.code === 2) {
            errorMsg = 'No se pudo determinar tu ubicación. Verifica que el GPS esté activado.';
          } else if (error.code === 3) {
            errorMsg = 'Tiempo de espera agotado. Inténtalo de nuevo.';
          }

          reject(new Error(errorMsg));
        },
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 60000
        }
      );
    });
  };

  // Load active time entry and today's total on mount
  useEffect(() => {
    loadActiveEntry();
    loadTodayTotal();
  }, []);

  // Update elapsed time regularly
  useEffect(() => {
    // Update immediately on mount or when activeEntry/todayTotalMinutes changes
    updateDisplayTime();

    if (!activeEntry) {
      // If no active entry, just show today's total (no need for frequent updates)
      return;
    }

    // When there's an active session, reload total every minute to show real-time progress
    const interval = setInterval(() => {
      loadTodayTotal(); // Reload total from backend (includes current session time)
    }, 60000); // Update every 60 seconds (1 minute)

    return () => clearInterval(interval);
  }, [activeEntry, todayTotalMinutes]);

  const updateDisplayTime = () => {
    // todayTotalMinutes already includes the active session time from backend
    const totalMinutes = Math.floor(todayTotalMinutes);

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    setElapsedTime(`${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`);
  };

  const loadActiveEntry = async () => {
    try {
      const entry = await timeEntriesAPI.getActive();
      setActiveEntry(entry);
    } catch (error) {
      // No active entry is fine
    }
  };

  const loadTodayTotal = async () => {
    try {
      const data = await timeEntriesAPI.getTodayTotal();
      setTodayTotalMinutes(data.total_minutes || 0);
    } catch (error) {
      // Error loading today total - ignore
    }
  };

  const handleClockIn = async () => {
    try {
      setIsLoading(true);

      // Get current location (required)
      const location = await getCurrentLocation();

      const entry = await timeEntriesAPI.clockIn(location);
      setActiveEntry(entry);

      addNotification({
        type: 'success',
        message: 'Jornada iniciada correctamente'
      });
    } catch (error) {
      // Check if it's a location error (browser geolocation)
      if (error.message && (error.message.includes('ubicación') || error.message.includes('GPS') || error.message.includes('geolocalización'))) {
        addNotification({
          type: 'error',
          message: error.message
        });
      // Check if it's a geofencing error from the server
      } else if (error.response?.data?.detail) {
        addNotification({
          type: 'error',
          message: error.response.data.detail
        });
      } else {
        addNotification({
          type: 'error',
          message: 'Error al iniciar la jornada'
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClockOutClick = () => {
    // Show confirmation dialog
    setShowClockOutConfirm(true);
  };

  const handleClockOut = async () => {
    try {
      setIsLoading(true);
      setShowClockOutConfirm(false);

      // Get current location (required)
      const location = await getCurrentLocation();

      await timeEntriesAPI.clockOut(null, location);
      setActiveEntry(null);

      // Reload today's total to get the updated accumulated time
      await loadTodayTotal();

      addNotification({
        type: 'success',
        message: 'Jornada finalizada correctamente'
      });
    } catch (error) {
      // Check if it's a location error (browser geolocation)
      if (error.message && (error.message.includes('ubicación') || error.message.includes('GPS') || error.message.includes('geolocalización'))) {
        addNotification({
          type: 'error',
          message: error.message
        });
      // Check if it's a geofencing error from the server
      } else if (error.response?.data?.detail) {
        addNotification({
          type: 'error',
          message: error.response.data.detail
        });
      } else {
        addNotification({
          type: 'error',
          message: 'Error al finalizar la jornada'
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (activeEntry) {
    // Show clock out button with timer
    return (
      <>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowEntriesModal(true)}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer"
            title="Ver registros del día"
          >
            {elapsedTime}
          </button>
          <button
            onClick={handleClockOutClick}
            disabled={isLoading}
            className="bg-green-500 hover:bg-green-600 text-white p-2 rounded-lg transition-colors disabled:opacity-50"
            title="Finalizar jornada"
          >
            <FiCheck className="h-6 w-6" />
          </button>
        </div>

        {/* Clock out confirmation dialog */}
        {showClockOutConfirm && (
          <ConfirmDialog
            title="Finalizar jornada"
            message={`¿Estás seguro de que deseas finalizar la jornada? Tiempo acumulado hoy: ${elapsedTime}`}
            confirmText="Finalizar"
            confirmStyle="success"
            onConfirm={handleClockOut}
            onCancel={() => setShowClockOutConfirm(false)}
          />
        )}

        {/* Time entries modal */}
        <TimeEntriesModal
          isOpen={showEntriesModal}
          onClose={() => setShowEntriesModal(false)}
        />
      </>
    );
  }

  // Show clock in button with pulsing animation (and total time if any)
  return (
    <>
      <div className="flex items-center space-x-2">
        {todayTotalMinutes > 0 && (
          <button
            onClick={() => setShowEntriesModal(true)}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer"
            title="Ver registros del día"
          >
            {elapsedTime}
          </button>
        )}
        <button
          onClick={handleClockIn}
          disabled={isLoading}
          className="relative bg-yellow-500 hover:bg-yellow-600 text-white p-2 rounded-lg transition-colors disabled:opacity-50"
          title="Iniciar jornada"
        >
          <FiClock className="h-6 w-6" />
          <span className="absolute -inset-1 rounded-lg bg-yellow-400 opacity-75 animate-ping pointer-events-none"></span>
        </button>
      </div>

      {/* Time entries modal */}
      <TimeEntriesModal
        isOpen={showEntriesModal}
        onClose={() => setShowEntriesModal(false)}
      />
    </>
  );
};

export default TimeClockButton;
